import { h, createApp } from "vue";
import singleSpaVue from "single-spa-vue";
import { requireEnv } from "@virtual-care/shared";

const isKnownZoomRuntimeIssue = (messageLike: unknown, stackLike?: unknown, filenameLike?: unknown): boolean => {
  const message = String(messageLike || "").toLowerCase();
  const stack = String(stackLike || "").toLowerCase();
  const filename = String(filenameLike || "").toLowerCase();

  const fromZoomBundle =
    filename.includes("zoom_videosdk") || stack.includes("zoom_videosdk") || stack.includes("index.umd.js");

  const knownSignature =
    message.includes("reading 'util'") ||
    message.includes("reading 'caps'") ||
    message.includes("shouldusewebrtc") ||
    stack.includes("issupportvideodecodehardwareacceleration");

  // filename is sometimes empty for errors raised deep in the Zoom UMD bundle; stack URL still matches.
  if (knownSignature && stack.includes("index.umd.js")) return true;

  return fromZoomBundle && knownSignature;
};

// Suppress non-critical Zoom telemetry errors (external logging services)
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason?.toString() || ''
  
  // List of known non-critical Zoom telemetry domains
  const zoomTelemetryDomains = [
    'log-external-gateway.zoom.us',
    'us06das.zoom.us',
    'rwciad3.iad.zoom.us',
    'rwcsjc3.sjc.zoom.us',
    'ajax error',
    'ERR_SOCKET_NOT_CONNECTED',
    'ERR_NAME_NOT_RESOLVED'
  ]
  
  const isZoomTelemetryError = zoomTelemetryDomains.some(domain => 
    reason.includes(domain)
  )
  
  if (isZoomTelemetryError) {
    console.debug('[NurseConsole] Suppressed Zoom telemetry error:', reason)
    event.preventDefault() // Prevent default error overlay
    return
  }
  
  // Let other errors bubble up
  console.error('[NurseConsole] Unhandled rejection:', event.reason)
})

// Diagnose cross-origin isolation status at the earliest possible point.
// This determines whether the Zoom SDK can use SharedArrayBuffer for its media pipeline.

// Pre-flight: Log critical build-time environment to detect Safari cache issues
console.log("[App] Build configuration:", {
  apiBaseUrl: requireEnv(process.env.VUE_APP_API_BASE_URL, 'VUE_APP_API_BASE_URL'),
  socketUrl: requireEnv(process.env.VUE_APP_SOCKET_URL, 'VUE_APP_SOCKET_URL'),
  buildTimestamp: Date.now(),
});

console.log("[App] crossOriginIsolated:", (window as any).crossOriginIsolated ?? "unknown");
console.log("[App] SharedArrayBuffer available:", typeof SharedArrayBuffer !== "undefined");

import { pinia } from "./nurse-console/store";
import router from "./nurse-console/router/router";
import vuetify from "@gwn/ui-design-system-shared/plugins/vuetify";
import { setInterfaces } from "./nurse-console/helpers/useInterface";
import App from "./nurse-console/App.vue";
import { getReportingInterface, getStandaloneInterfaces } from "./interfaces";

const vueLifecycles = singleSpaVue({
  createApp,
  appOptions: {
    render() {
      // In guac shell, `window.interfaces` is set by the host app before mounting.
      // `this.interfaces` is a single-spa custom prop only available when embedded —
      // accessing it here when running standalone triggers a Vue property warning.
      setInterfaces((window as any).interfaces ?? getStandaloneInterfaces());

      return h(App);
    },
  },
  handleInstance(app) {
    const reportingInterface = getReportingInterface();
    app.use(router);
    app.use(pinia);
    app.use(vuetify);
    app.config.errorHandler = (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      const stack = err instanceof Error ? err.stack : undefined;
      if (isKnownZoomRuntimeIssue(msg, stack, undefined)) {
        console.warn("[App] Suppressed known Zoom error in Vue handler:", msg);
        return;
      }
      console.error(err);
      reportingInterface.captureException(err instanceof Error ? err : new Error(String(err)));
    };
  },
});

export const bootstrap = vueLifecycles.bootstrap;
export const mount = vueLifecycles.mount;
export const unmount = vueLifecycles.unmount;

// Standalone mode: mount directly when not embedded in single-spa shell
if (!(window as any).singleSpaNavigate) {
  console.log("[App] Running in standalone mode - mounting directly");
  const app = createApp({
    render() {
      setInterfaces((window as any).interfaces ?? getStandaloneInterfaces());
      return h(App);
    },
  });

  const reportingInterface = getReportingInterface();
  app.use(router);
  app.use(pinia);
  app.use(vuetify);
  app.config.errorHandler = (err: unknown) => {
    const msg = err instanceof Error ? err.message : String(err);
    const stack = err instanceof Error ? err.stack : undefined;
    if (isKnownZoomRuntimeIssue(msg, stack, undefined)) {
      console.warn("[App] Suppressed known Zoom error in Vue handler:", msg);
      return;
    }
    console.error(err);
    reportingInterface.captureException(err instanceof Error ? err : new Error(String(err)));
  };

  app.mount("#app");
  console.log("[App] Mounted in standalone mode");
}
